/**
 * BrandFooter — YDM attribution strip.
 *
 * On-brand: lowercase `ydm` wordmark in YDM yellow, italic Prompt, the real
 * slogan "MAKE IT UNSTOPPABLE" as the secondary line.
 *
 * Two variants:
 *   - `attribution` (default) — inline strip for auth screens
 *   - `full` — bordered footer bar (year + domain) for dashboard pages
 */

import React from 'react';
import { clsx } from 'clsx';

export interface BrandFooterProps {
  variant?: 'attribution' | 'full';
  /** Match the surrounding background so type contrast stays brand-correct. */
  bg?: 'light' | 'dark' | 'yellow';
  className?: string;
}

const YEAR = '2026';

export const BrandFooter: React.FC<BrandFooterProps> = ({
  variant = 'attribution',
  bg = 'light',
  className,
}) => {
  // Per the brand guideline the `ydm` mark should be black on yellow, yellow
  // on black, and black on white. The footer body text mirrors that.
  const wordmarkColor =
    bg === 'dark' ? 'text-primary-500' : 'text-ink-950';
  const bodyText =
    bg === 'dark' ? 'text-ink-300'
    : bg === 'yellow' ? 'text-ink-900'
    : 'text-ink-500';
  // Measured on the rendered page, per variant — the same token is right on
  // one ground and invisible on another. On WHITE, ink-300 was 2.10:1 and
  // ink-400 3.54:1; ink-500 is the shade the body text already uses there and
  // the first that clears 4.5:1. A separator wants to be quieter than the words
  // it separates, and below the floor "quieter" is not on offer.
  const dotColor =
    bg === 'dark' ? 'text-ink-600'
    : bg === 'yellow' ? 'text-ink-700'
    : 'text-ink-500';
  const wordmark = (
    <span className={clsx('font-display italic font-bold tracking-tight lowercase', wordmarkColor)}>
      ydm
    </span>
  );

  if (variant === 'attribution') {
    return (
      <p
        className={clsx(
          'text-xs text-center inline-flex items-center justify-center gap-1.5',
          bodyText,
          className
        )}
      >
        <span>Powered by</span>
        {wordmark}
        <span className={dotColor}>·</span>
        <span className="font-display italic">Make It Unstoppable</span>
      </p>
    );
  }

  return (
    <footer
      className={clsx(
        'px-4 py-3 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs',
        bg === 'dark'
          ? 'border-t border-ink-700 bg-ink-950 text-ink-300'
          : bg === 'yellow'
          ? 'border-t border-ink-950 bg-primary-500 text-ink-900'
          : 'border-t border-gray-200 bg-white text-ink-500',
        className
      )}
    >
      <div className="inline-flex items-center gap-1.5">
        <span>Powered by</span>
        {wordmark}
        <span className={dotColor}>·</span>
        <span className="font-display italic">Make It Unstoppable</span>
      </div>
      <div className={bg === 'dark' ? 'text-ink-500' : bg === 'yellow' ? 'text-ink-800' : 'text-ink-500'}>
        © {YEAR} YDM Thailand · ydm.family
      </div>
    </footer>
  );
};

export default BrandFooter;
